import { DialogComponent } from '@theme/dialog';

/**
 * Storefront copy, from the translations the theme exposes on the global
 * Theme object in snippets/scripts.liquid.
 *
 * Shopify's `t` filter escapes any translation whose key does not end in
 * _html, so a string with an apostrophe arrives here as "we&#39;ll". This copy
 * is written out with textContent, which shows entities literally, so decode
 * once here rather than at every call site.
 * @param {string} key
 */
function t(key) {
  const copy = (typeof Theme !== 'undefined' && Theme.translations && Theme.translations[key]) || '';
  return decodeEntities(copy);
}

/**
 * A detached textarea decodes entities without parsing markup: its content is
 * plain text by definition, so nothing in the string can become an element.
 * @param {string} value
 */
function decodeEntities(value) {
  if (!value || !value.includes('&')) return value;

  const decoder = document.createElement('textarea');
  decoder.innerHTML = value;
  return decoder.value;
}

/**
 * Wishlist drawer.
 *
 * Extends the theme's DialogComponent so it behaves exactly like the cart
 * drawer — same open animation, focus trap, scroll lock and escape handling.
 * Contents come from the Swym JS SDK; Swym's own drawer is switched off via
 * the app embed's "Show Swym UI" setting.
 *
 * Item shape returned by swat.fetch (confirmed against this store):
 *   dt  product title      iu  image url       du  product url
 *   pr  price in MAJOR units (75, not 7500)    vi  variant title ("2XS")
 *   epi variant id         empi product id
 */
class WishlistDrawerComponent extends DialogComponent {
  #swat = null;
  #items = [];
  /** Items removed here that the server may not have caught up on yet. */
  #pendingRemovals = new Set();
  #hasRendered = false;
  /**
   * Product data by url. Swym's payload carries neither tags nor the variant
   * list, so each product is looked up once and cached.
   */
  #productCache = new Map();

  connectedCallback() {
    super.connectedCallback();

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => {
      this.#swat = swat || window._swat;
      if (this.refs.dialog?.open) this.load();
    });

    // Hearts elsewhere on the page change the same list.
    document.addEventListener('wishlist:updated', this.#onExternalUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('wishlist:updated', this.#onExternalUpdate);
  }

  #onExternalUpdate = () => {
    if (this.refs.dialog?.open) this.load();
  };

  open() {
    this.showDialog();
    this.load();
  }

  close() {
    this.closeDialog();
  }

  /** Pull the current list and paint it. */
  load() {
    if (!this.#swat || typeof this.#swat.fetch !== 'function') {
      // SDK not ready yet; the SwymCallbacks handler will call back in.
      this.#setState({ loadingText: t('wishlist_loading') });
      return;
    }

    if (!this.#hasRendered) this.#setState({ loadingText: t('wishlist_loading') });

    this.#swat.fetch(
      (items) => {
        const list = Array.isArray(items) ? items : [];

        // Anything the server has genuinely dropped is settled, so stop
        // holding it back.
        for (const key of [...this.#pendingRemovals]) {
          if (!list.some((item) => String(item.epi) === key)) this.#pendingRemovals.delete(key);
        }

        this.#items = list.filter((item) => !this.#pendingRemovals.has(String(item.epi)));
        this.#loadProducts(this.#items).then(() => this.#render());
      },
      () => {
        this.#setState({ loadingText: t('wishlist_load_error') });
      }
    );
  }

  /**
   * Look up each product, in parallel and cached, for its tags and variants.
   * A failed lookup leaves the item bare, and the row falls back to the
   * variant Swym stored.
   * @param {Array<Record<string, any>>} items
   */
  async #loadProducts(items) {
    await Promise.all(
      items.map(async (item) => {
        if (!item.du) return;

        if (this.#productCache.has(item.du)) {
          item.product = this.#productCache.get(item.du);
          return;
        }

        let data = { tags: [], variants: [], singleOption: true };
        try {
          const response = await fetch(`${item.du}.js`);
          if (response.ok) {
            const product = await response.json();
            data = {
              tags: Array.isArray(product.tags) ? product.tags.map((t) => String(t).toLowerCase()) : [],
              variants: Array.isArray(product.variants) ? product.variants : [],
              singleOption: !Array.isArray(product.options) || product.options.length === 1
            };
          }
        } catch (error) {
          // Leave bare.
        }

        this.#productCache.set(item.du, data);
        item.product = data;
      })
    );
  }

  #setState({ loadingText = null, showList = false, showEmpty = false } = {}) {
    const { loading, list, empty, footer } = this.refs;
    if (loading) {
      loading.hidden = loadingText === null;
      if (loadingText) loading.textContent = loadingText;
    }
    if (list) list.hidden = !showList;
    if (empty) empty.hidden = !showEmpty;
    if (footer) footer.hidden = !showList;
  }

  #money(value) {
    const currency = this.dataset.currency || 'GBP';
    const amount = Number(value) || 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2
      }).format(amount);
    } catch (error) {
      return amount.toString();
    }
  }

  #render() {
    const { list, headingCount, guestNote } = this.refs;
    const count = this.#items.length;

    if (headingCount) headingCount.textContent = count ? `(${count})` : '';

    if (!count) {
      if (list) list.innerHTML = '';
      this.#setState({ showEmpty: true });
      this.#hasRendered = true;
      return;
    }

    if (list) {
      list.innerHTML = '';
      for (const item of this.#items) list.appendChild(this.#row(item));
    }

    this.#setState({ showList: true });
    this.#hasRendered = true;

    // Only nudge guests, and only when they have something to lose.
    if (guestNote) {
      const loggedIn = Boolean(this.#swat?.platform?.isLoggedIn?.());
      guestNote.hidden = loggedIn;
    }
  }

  /** @param {Record<string, any>} item */
  #row(item) {
    // Matches the PDP, where a coming-soon tag disables buying. Here it also
    // withholds the price and the link, so an unlaunched product gives
    // nothing away.
    const product = item.product || { tags: [], variants: [], singleOption: true };
    const comingSoon = product.tags.includes('coming-soon');

    // A saved variant that no longer exists can't be bought either, so it counts
    // as out of stock. If the product lookup failed there are no variants to
    // judge by, so fall through to Add to bag rather than guess.
    const savedVariant = product.variants.find((v) => String(v.id) === String(item.epi));
    const outOfStock = !comingSoon && product.variants.length > 0 && !(savedVariant && savedVariant.available);

    const li = document.createElement('li');
    li.className = comingSoon ? 'wishlist-drawer__item wishlist-drawer__item--coming-soon' : 'wishlist-drawer__item';
    li.dataset.epi = item.epi;
    li.dataset.empi = item.empi;

    const media = document.createElement(comingSoon ? 'span' : 'a');
    media.className = 'wishlist-drawer__item-media';
    if (!comingSoon) media.href = item.du || '#';
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (item.iu) {
      const img = document.createElement('img');
      img.src = item.iu;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 76;
      media.appendChild(img);
    }

    const details = document.createElement('div');
    details.className = 'wishlist-drawer__item-details';

    const title = document.createElement(comingSoon ? 'p' : 'a');
    title.className = 'wishlist-drawer__item-title';
    if (!comingSoon) title.href = item.du || '#';
    title.textContent = item.dt || '';
    details.appendChild(title);

    if (item.vi) {
      const variant = document.createElement('p');
      variant.className = 'wishlist-drawer__item-variant';
      variant.textContent = item.vi;
      details.appendChild(variant);
    }

    if (!comingSoon) {
      const price = document.createElement('p');
      price.className = 'wishlist-drawer__item-price';
      price.textContent = this.#money(item.pr);
      details.appendChild(price);
    }

    const actions = document.createElement('div');
    actions.className = 'wishlist-drawer__item-actions';

    if (comingSoon) {
      const soon = document.createElement('p');
      soon.className = 'wishlist-drawer__soon';
      soon.textContent = t('wishlist_coming_soon');
      actions.appendChild(soon);
    } else if (outOfStock) {
      const oos = document.createElement('p');
      oos.className = 'wishlist-drawer__oos';
      oos.textContent = t('wishlist_out_of_stock');
      actions.appendChild(oos);
      if (savedVariant && this.#canNotify()) actions.appendChild(this.#notifyControl(item));
    } else {
      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'wishlist-drawer__add';
      add.textContent = t('wishlist_add_to_bag');
      add.addEventListener('click', () => this.#addToCart(item, add));
      actions.appendChild(add);
    }

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'wishlist-drawer__remove';
    remove.setAttribute(
      'aria-label',
      item.dt ? t('wishlist_remove').replace('[title]', item.dt) : t('wishlist_remove_generic')
    );
    remove.innerHTML =
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="m3 3 10 10M13 3 3 13" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>';
    remove.addEventListener('click', (event) => {
      // The row is about to be detached. Without this the dialog's
      // document-level handler sees a target outside itself and closes.
      event.stopPropagation();
      this.#remove(item, li);
    });

    details.appendChild(actions);
    li.appendChild(media);
    li.appendChild(details);
    li.appendChild(remove);
    return li;
  }

  /** AMP Back in Stock's widget exposes BIS.create on every page. */
  #canNotify() {
    return typeof window.BIS !== 'undefined' && typeof window.BIS.create === 'function';
  }

  /**
   * "Notify me when available" for a saved size that's out of stock, through
   * AMP Back in Stock. AMP's own popup only works on a product page, so this
   * registers directly with BIS.create, which works anywhere. Logged-in
   * customers are registered in one click; guests enter an email first.
   * Marketing consent is never assumed — accepts_marketing is not sent.
   * @param {Record<string, any>} item
   */
  #notifyControl(item) {
    const wrap = document.createElement('div');
    wrap.className = 'wishlist-drawer__notify';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'wishlist-drawer__notify-toggle';
    toggle.textContent = t('wishlist_notify_me');

    const form = document.createElement('form');
    form.className = 'wishlist-drawer__notify-form';
    form.hidden = true;

    const input = document.createElement('input');
    input.type = 'email';
    input.required = true;
    input.autocomplete = 'email';
    input.className = 'wishlist-drawer__notify-input';
    input.placeholder = t('wishlist_notify_email');
    input.setAttribute('aria-label', t('wishlist_notify_email'));

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'wishlist-drawer__notify-submit';
    submit.textContent = t('wishlist_notify_submit');

    const status = document.createElement('p');
    status.className = 'wishlist-drawer__notify-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    form.append(input, submit);

    const send = (email, control) => {
      control.disabled = true;
      status.textContent = '';

      const finish = (ok) => {
        clearTimeout(timer);
        if (ok) {
          toggle.hidden = true;
          form.hidden = true;
          status.textContent = t('wishlist_notify_success');
        } else {
          control.disabled = false;
          status.textContent = t('wishlist_notify_error');
        }
      };

      // The widget's promise has no failure path of its own, so don't wait forever.
      const timer = setTimeout(() => finish(false), 12000);

      try {
        const request = window.BIS.create(email, Number(item.epi), Number(item.empi));
        if (request && typeof request.then === 'function') {
          request.then((...args) => finish(this.#bisSucceeded(args)));
        } else {
          finish(false);
        }
      } catch (error) {
        finish(false);
      }
    };

    // Elements are hidden, never removed, on click: removing a clicked element
    // makes the dialog read it as a click outside and close (see #remove).
    toggle.addEventListener('click', (event) => {
      event.stopPropagation();
      const email = this.dataset.customerEmail;
      if (email) {
        send(email, toggle);
      } else {
        toggle.hidden = true;
        form.hidden = false;
        input.focus();
      }
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const email = input.value.trim();
      if (email) send(email, submit);
    });

    wrap.append(toggle, form, status);
    return wrap;
  }

  /**
   * BIS.create resolves through BIS.Promise. Depending on the widget build the
   * callback receives the parsed response, or BIS.request's (isError,
   * responseText) pair — accept either. Success is { status: 'OK' }.
   * @param {unknown[]} args
   */
  #bisSucceeded(args) {
    let data = args[0];
    if (typeof data === 'boolean') {
      if (data) return false;
      try {
        data = JSON.parse(String(args[1]));
      } catch (error) {
        return true; // HTTP 200 without a readable body
      }
    } else if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        return false;
      }
    }
    return Boolean(data) && String(data.status).toUpperCase() === 'OK';
  }

  #remove(item, row) {
    if (!this.#swat) return;
    row.setAttribute('aria-busy', 'true');
    this.#pendingRemovals.add(String(item.epi));

    // Swym removes the item server side but does not reliably fire its success
    // callback, so waiting on it leaves a stale row. Paint straight away and
    // reconcile against the server afterwards.
    this.#items = this.#items.filter((i) => String(i.epi) !== String(item.epi));
    this.#render();
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: 'drawer' } }));

    const reconcile = () => this.load();

    try {
      this.#swat.removeFromWishList({ empi: item.empi, epi: item.epi, du: item.du }, reconcile, reconcile);
    } catch (error) {
      reconcile();
    }

    // Backstop in case neither callback arrives.
    setTimeout(reconcile, 2500);
  }

  #addToCart(item, control) {
    const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
    const isButton = control.tagName === 'BUTTON';
    const original = isButton ? control.textContent : '';
    control.disabled = true;
    if (isButton) control.textContent = t('wishlist_adding');

    const sections = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset?.sectionId) sections.push(el.dataset.sectionId);
    });

    const body = { items: [{ id: Number(item.epi), quantity: 1 }] };
    if (sections.length) body.sections = sections.join(',');

    fetch(routes.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then((response) => response.json().then((payload) => {
        if (!response.ok || payload.status) throw new Error(payload.description || '');
        return payload;
      }))
      .then((payload) =>
        fetch(routes.cart_url ? `${routes.cart_url}.js` : '/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'wishlist-drawer',
                  data: {
                    source: 'product-form-component',
                    itemCount: 1,
                    variantId: String(item.epi),
                    productId: String(item.empi),
                    sections: payload.sections
                  }
                }
              })
            );

            // Hand over to the cart drawer rather than stacking two drawers.
            this.closeDialog();
            const cartDrawer = document.querySelector('cart-drawer-component');
            if (cartDrawer?.open) cartDrawer.open();
          })
      )
      .catch(() => {
        if (isButton) control.textContent = t('wishlist_add_error');
        else control.value = '';
      })
      .finally(() => {
        control.disabled = false;
        if (isButton && control.textContent === t('wishlist_adding')) control.textContent = original;
      });
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
